import React from 'react'
import PropTypes from 'prop-types'
import { Form, Input, InputNumber, Switch, Button, Space, Typography, Tag, Spin } from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { SelectF } from '@/i2antdV5'
import TagSelectF from '@/i2antdV5/TagSelectF'
import { getNotificationTemplateVarKeys } from '@/service/notification'
import StaffAgentTargetPick, {
  notificationTargetIsStaff
} from './StaffAgentTargetPick'

const FormItem = Form.Item

/**
 * 短信模版变量 key：`notifyCategory` 对应 GET `/common/notification/{category}/template_var_keys`。
 * 含短信且已填模板 ID、且表单中已配置 templateVarSlots 时提交 `templateVarKeys`。
 */

function allowedKeySet (allowedKeys) {
  if (allowedKeys === undefined) return null
  if (!Array.isArray(allowedKeys)) return null
  return new Set(allowedKeys.map(String))
}

/**
 * `allowedKeys` 非空则仅保留其中的 key；为空则不按服务端列表过滤（仅去重），便于回填早于选项加载完成。
 */
export function sanitizeTemplateVarKeySequence (seq, allowedKeys) {
  const allowed = allowedKeySet(allowedKeys)
  const seen = new Set()
  const out = []
  if (!Array.isArray(seq)) return out
  for (const k of seq) {
    const s = String(k)
    if (allowed && !allowed.has(s)) continue
    if (seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out
}

export function normalizeTemplateVarKeys (arr, allowedKeys) {
  return sanitizeTemplateVarKeySequence(arr, allowedKeys)
}

export function isTemplateVarSlotsConfigured (slots) {
  return Array.isArray(slots) && slots.length > 0
}

export function normalizeTemplateVarSlotsIfConfigured (slots, allowedKeys) {
  if (!isTemplateVarSlotsConfigured(slots)) return null
  const allowed = allowedKeySet(allowedKeys)

  if (typeof slots[0] === 'string') {
    const order = sanitizeTemplateVarKeySequence(slots, allowedKeys)
    return order.map((key) => ({ key, pass: true }))
  }

  const order = []
  const passByKey = Object.create(null)

  for (const item of slots) {
    if (item == null || typeof item !== 'object') continue
    const key = item.key != null ? String(item.key) : ''
    if (allowed && !allowed.has(key)) continue
    if (order.includes(key)) continue
    order.push(key)
    passByKey[key] = item.pass !== false && item.send !== false
  }

  const mergedOrder = sanitizeTemplateVarKeySequence(order, allowedKeys)
  return mergedOrder.map((key) => ({
    key,
    pass: Object.prototype.hasOwnProperty.call(passByKey, key)
      ? passByKey[key]
      : true
  }))
}

function expandTemplateVarSlotsForEditor (value, allowedKeys) {
  return (
    normalizeTemplateVarSlotsIfConfigured(value, allowedKeys) ??
    (Array.isArray(allowedKeys) && allowedKeys.length > 0
      ? allowedKeys.map((k) => ({ key: String(k), pass: true }))
      : [])
  )
}

/**
 * @param {string[]} sequence
 * @param {string} key
 * @param {string[]} allowedKeys 接口返回的可选 key；空则不拦截（一般无按钮可点）
 */
export function appendClickToTemplateVarSequence (sequence, key, allowedKeys) {
  const allowed = allowedKeySet(allowedKeys)
  const k = String(key)
  if (allowed && !allowed.has(k)) return Array.isArray(sequence) ? [...sequence] : []
  const prev = Array.isArray(sequence) ? sequence : []
  const filtered = prev.filter((x) => String(x) !== k)
  return [...filtered, k]
}

function pickTemplateVarLabelFromItem (item) {
  if (item == null || typeof item !== 'object') return ''
  const label =
    item.label ??
    item.name ??
    item.title ??
    item.text ??
    item.desc ??
    ''
  return label !== '' ? String(label) : ''
}

function pickTemplateVarKeyFromItem (item) {
  if (item == null || typeof item !== 'object') return ''
  const keyRaw =
    item.key ??
    item.value ??
    item.code ??
    item.id ??
    item.name
  return keyRaw !== undefined && keyRaw !== null ? String(keyRaw) : ''
}

/**
 * 解析 GET .../template_var_keys 的 `data`（兼容多种数组/对象结构）。
 */
export function parseNotificationTemplateVarKeysPayload (data) {
  const labelByKey = Object.create(null)
  const keys = []

  const pushPair = (keyStr, labelStr) => {
    const k = String(keyStr).trim()
    if (k === '') return
    if (!keys.includes(k)) keys.push(k)
    const lb =
      labelStr != null && String(labelStr).trim() !== ''
        ? String(labelStr)
        : k
    labelByKey[k] = lb
  }

  let raw = data
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    raw =
      raw.items ??
      raw.list ??
      raw.records ??
      raw.templateVarKeys ??
      raw.template_var_keys ??
      raw.keys ??
      raw.data
  }

  if (!raw) {
    return { keys: [], labelByKey: {} }
  }

  if (Array.isArray(raw)) {
    for (const row of raw) {
      if (row == null) continue
      if (typeof row === 'string' || typeof row === 'number') {
        pushPair(row, String(row))
      } else if (typeof row === 'object') {
        const k = pickTemplateVarKeyFromItem(row)
        if (k === '') continue
        const lb = pickTemplateVarLabelFromItem(row)
        pushPair(k, lb || k)
      }
    }
    return { keys, labelByKey }
  }

  return { keys: [], labelByKey: {} }
}

/** 与列表 notifyCategory 一致：编辑取规则字段，新建取 defaultScene */
export function resolveNotifyCategoryFromBaseProps (baseProps = {}) {
  const bp = baseProps || {}
  const er = bp.editingRule
  if (er && typeof er === 'object') {
    if (er.notifyCategory != null && er.notifyCategory !== '') {
      return String(er.notifyCategory)
    }
    if (er.notify_category != null && er.notify_category !== '') {
      return String(er.notify_category)
    }
  }
  if (bp.defaultScene != null && bp.defaultScene !== '') {
    return String(bp.defaultScene)
  }
  return ''
}

/** Modal 提交：`loaded === true` 时用解析结果（可为空数组）；未就绪则不套白名单由校验拦住 */
export function parseTemplateVarAllowedKeysJson (raw, loaded) {
  if (!loaded) return undefined
  if (raw == null || raw === '') return []
  if (Array.isArray(raw)) return raw.map(String).filter((s) => s !== '')
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (s === '') return []
    try {
      const p = JSON.parse(s)
      return Array.isArray(p) ? p.map(String).filter((x) => x !== '') : []
    } catch {
      return []
    }
  }
  return []
}

/** 接口 templateVarKeys：数组，或 JSON 字符串（与 targets 类似） */
function parseNotificationRuleTemplateVarKeysRaw (raw) {
  if (raw == null || raw === '') return null
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (s === '') return null
    try {
      const parsed = JSON.parse(s)
      return Array.isArray(parsed) ? parsed : null
    } catch {
      return null
    }
  }
  return null
}

/** 规则记录 → 表单 templateVarSlots（无接口字段时不默认顺序） */
export function ruleRecordToTemplateVarSlots (rule, allowedKeys) {
  if (!rule || typeof rule !== 'object') {
    return undefined
  }
  const raw =
    rule.templateVarKeys != null ? rule.templateVarKeys : rule.template_var_keys
  const keysList = parseNotificationRuleTemplateVarKeysRaw(raw)

  if (keysList == null || keysList.length === 0) {
    return undefined
  }

  if (
    typeof keysList[0] === 'object' &&
    keysList[0] !== null &&
    keysList[0].key != null
  ) {
    const slotsFromApi = keysList.map((x) => ({
      key: String(x.key),
      pass: x.pass !== false && x.send !== false
    }))
    return (
      normalizeTemplateVarSlotsIfConfigured(slotsFromApi, allowedKeys) ??
      undefined
    )
  }

  const passRaw =
    rule.templateVarPass != null ? rule.templateVarPass : rule.template_var_pass

  let passByKey = null
  if (passRaw && typeof passRaw === 'object' && !Array.isArray(passRaw)) {
    passByKey = {}
    for (const k of Object.keys(passRaw)) {
      const ks = String(k)
      const v = passRaw[k]
      passByKey[ks] = v !== false && v !== 'false' && v !== 0 && v !== '0'
    }
  }

  const strKeys = keysList.map((x) => String(x)).filter((s) => s !== '')
  const baseOrder = sanitizeTemplateVarKeySequence(strKeys, allowedKeys)
  return baseOrder.map((key) => ({
    key,
    pass:
      passByKey == null
        ? true
        : Object.prototype.hasOwnProperty.call(passByKey, key)
          ? passByKey[key]
          : true
  }))
}

/** 模版变量：按钮顺序仅提交点选项，不自动补全接口列表中的其余 key */
function TemplateVarSlotsEditor ({
  value,
  onChange,
  allowedKeys = [],
  labelByKey = {},
  fetching = false
}) {
  const valueRef = React.useRef(value)
  valueRef.current = value

  const internalUpdateRef = React.useRef(false)
  const valueSyncSig = React.useMemo(() => {
    const cfg = normalizeTemplateVarSlotsIfConfigured(value, allowedKeys)
    return cfg ? JSON.stringify(cfg) : ''
  }, [value, allowedKeys])
  const configured = valueSyncSig !== ''

  const [clickSeq, setClickSeq] = React.useState([])

  React.useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false
      return
    }
    if (valueSyncSig === '') {
      setClickSeq([])
      return
    }
    try {
      const cfg = JSON.parse(valueSyncSig)
      if (Array.isArray(cfg)) {
        setClickSeq(cfg.map((s) => s.key))
      }
    } catch {
      setClickSeq([])
    }
  }, [valueSyncSig])

  const commitClickSeq = (nextSeq) => {
    const latest = expandTemplateVarSlotsForEditor(valueRef.current, allowedKeys)
    const passMap = Object.fromEntries(latest.map((s) => [s.key, s.pass]))
    const ordered = sanitizeTemplateVarKeySequence(nextSeq, allowedKeys)
    const newSlots = ordered.map((k) => ({
      key: k,
      pass: passMap[k] !== false
    }))
    internalUpdateRef.current = true
    const payload = newSlots.length > 0 ? newSlots : undefined
    if (typeof onChange === 'function') {
      onChange(payload)
    }
  }

  const getCurrentClickSeq = () => {
    const configuredSlots = normalizeTemplateVarSlotsIfConfigured(
      valueRef.current,
      allowedKeys
    )
    if (configuredSlots) {
      return configuredSlots.map((slot) => slot.key)
    }
    return clickSeq
  }

  const handlePick = (key) => {
    const nextSeq = appendClickToTemplateVarSequence(
      getCurrentClickSeq(),
      key,
      allowedKeys
    )
    setClickSeq(nextSeq)
    commitClickSeq(nextSeq)
  }

  const handleRemoveFromClickSeq = (key) => {
    const nextSeq = getCurrentClickSeq().filter((k) => k !== key)
    setClickSeq(nextSeq)
    if (nextSeq.length === 0) {
      internalUpdateRef.current = true
      if (typeof onChange === 'function') {
        onChange(undefined)
      }
    } else {
      commitClickSeq(nextSeq)
    }
  }

  const resolveLabel = (k) =>
    (labelByKey && labelByKey[k]) != null && labelByKey[k] !== ''
      ? labelByKey[k]
      : k

  const pickSeqLabel =
    clickSeq.length === 0
      ? configured
        ? '可继续点击按钮调整顺序'
        : '尚未点选；配置后才会在请求中带上变量顺序'
      : clickSeq.map((k, i) => `${i + 1}.${resolveLabel(k)}`).join(' → ')

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {fetching && (!allowedKeys || allowedKeys.length === 0) ? (
        <Spin tip="加载模版变量选项…" />
      ) : null}
      <Space wrap>
        {(allowedKeys || []).map((k) => (
          <Button
            key={k}
            type="default"
            disabled={fetching}
            onClick={() => handlePick(k)}
          >
            {resolveLabel(k)}
          </Button>
        ))}
      </Space>

      <div
        style={{
          padding: '10px 12px',
          background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
          borderRadius: 6,
          border: '1px solid var(--ant-color-border-secondary, #f0f0f0)'
        }}
      >
        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          你选择的点击顺序（先点的在前）
        </Typography.Text>
        {clickSeq.length === 0 ? (
          <Typography.Text type="secondary">{pickSeqLabel}</Typography.Text>
        ) : (
          <>
            <Space wrap size={[8, 8]}>
              {clickSeq.map((k, i) => (
                <Tag
                  key={k}
                  color="blue"
                  closable
                  onClose={(e) => {
                    if (e && typeof e.preventDefault === 'function') {
                      e.preventDefault()
                    }
                    handleRemoveFromClickSeq(k)
                  }}
                >
                  第{i + 1}个 {resolveLabel(k)}
                </Tag>
              ))}
            </Space>
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              {pickSeqLabel}
            </Typography.Text>
          </>
        )}
      </div>
    </Space>
  )
}

/** 表单标量枚举值 → TagSelectF（radio）所需数组形态 */
function scalarToTagRadioValue (v) {
  if (v === undefined || v === null || v === '') return []
  return [String(v)]
}

/** TagSelectF（radio）onChange 数组 → 表单存库的标量 */
function tagRadioValueToScalar (arr) {
  if (!Array.isArray(arr) || arr.length === 0) return undefined
  return String(arr[0])
}

/** channels 是否包含短信（与枚举 value 大小写无关） */
export function channelsIncludeSms (channels) {
  if (!Array.isArray(channels)) return false
  return channels.some((c) => String(c).toUpperCase() === 'SMS')
}

/** 规则接口记录 → 表单初始值（含下划线字段兼容） */
export function ruleRecordToFormValues (rule) {
  if (!rule || typeof rule !== 'object') return null
  const channels = Array.isArray(rule.channels)
    ? rule.channels.map((c) => String(c))
    : []
  const targets = Array.isArray(rule.targets)
    ? rule.targets.map((t) => {
      const targetNameRaw =
        t.targetName !== undefined && t.targetName !== null
          ? t.targetName
          : t.target_name !== undefined && t.target_name !== null
            ? t.target_name
            : ''
      return {
        targetId: t.targetId != null ? t.targetId : t.target_id,
        phone: t.phone != null ? String(t.phone) : '',
        targetName: targetNameRaw !== '' ? String(targetNameRaw).trim() : ''
      }
    })
    : []
  return {
    scene:
      rule.notifyCategory != null && rule.notifyCategory !== ''
        ? String(rule.notifyCategory)
        : rule.notify_category != null && rule.notify_category !== ''
          ? String(rule.notify_category)
          : rule.scene != null
            ? String(rule.scene)
            : '',
    name: rule.name != null ? String(rule.name) : '',
    targetType: rule.targetType != null ? String(rule.targetType) : '',
    offsetValue:
      rule.offsetValue !== undefined && rule.offsetValue !== null
        ? rule.offsetValue
        : (rule.offset_value !== undefined && rule.offset_value !== null ? rule.offset_value : 0),
    offsetUnit: String(
      rule.offsetUnit != null ? rule.offsetUnit : (rule.offset_unit != null ? rule.offset_unit : '')
    ),
    lifecycle:
      rule.lifecycle != null && rule.lifecycle !== ''
        ? String(rule.lifecycle)
        : rule.notificationLifecycle != null && rule.notificationLifecycle !== ''
          ? String(rule.notificationLifecycle)
          : rule.notification_lifecycle != null && rule.notification_lifecycle !== ''
            ? String(rule.notification_lifecycle)
            : '',
    channels,
    templateId:
      rule.templateId != null
        ? String(rule.templateId)
        : (rule.template_id != null ? String(rule.template_id) : ''),
    enabled: rule.enabled !== false,
    storeId:
      rule.storeId != null && rule.storeId !== ''
        ? String(rule.storeId)
        : undefined,
    targets,
    ...(() => {
      const templateVarSlots = ruleRecordToTemplateVarSlots(rule)
      return templateVarSlots != null ? { templateVarSlots } : {}
    })()
  }
}

/**
 * 表单由 WrappedModal 注入 `form`，并与 Modal 共用一份业务入参对象（见 `baseProps`）。
 *
 * **baseProps（与列表/编辑处传入 NotificationRuleModal 的字段一致）**
 * - `defaultScene` `sceneLabel` `editingRule`
 * - `storeOptions` `defaultStoreId`
 * - `channelOptions` `targetTypeOptions` `offsetUnitOptions` `lifecycleOptions`
 * - `chuanglanTemplateOptions` 创蓝短信模板（option：id=模板 value，value=label）
 * - `onSuccess` `onCancel` 等仅 Modal 使用；表单不读也可保留在同对象上。
 */
class NotificationRuleForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    formKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** 与 Modal 壳层一致：create | edit（由 Modal 解析后传入） */
    actType: PropTypes.oneOf(['create', 'edit']),
    /** 与 NotificationRuleModal 的 baseProps 同源：门店/渠道/枚举项、editingRule、onSuccess 等业务字段 */
    baseProps: PropTypes.object
  }

  static defaultProps = {
    formKey: '',
    actType: 'create',
    baseProps: {}
  }

  state = {
    templateVarMeta: { keys: [], labelByKey: {}, fetching: false }
  }

  _templateVarFetchSeq = 0

  componentDidMount () {
    this.fetchTemplateVarKeys()
  }

  fetchTemplateVarKeys = async () => {
    const cat = resolveNotifyCategoryFromBaseProps(this.props.baseProps)
    const { form } = this.props

    if (!cat) {
      this._templateVarFetchSeq += 1
      this.setState({
        templateVarMeta: { keys: [], labelByKey: {}, fetching: false }
      })
      if (form && form.current) {
        form.current.setFieldsValue({
          _templateVarAllowedKeysJson: '[]',
          _templateVarKeysOptsLoaded: '1'
        })
      }
      return
    }

    const reqId = ++this._templateVarFetchSeq
    this.setState({
      templateVarMeta: { keys: [], labelByKey: {}, fetching: true }
    })
    if (form && form.current) {
      form.current.setFieldsValue({ _templateVarKeysOptsLoaded: '0' })
    }

    try {
      const { data } = await getNotificationTemplateVarKeys(cat)
      if (reqId !== this._templateVarFetchSeq) return

      const parsed = parseNotificationTemplateVarKeysPayload(data)
      if (reqId !== this._templateVarFetchSeq) return

      this.setState({
        templateVarMeta: {
          keys: parsed.keys,
          labelByKey: parsed.labelByKey,
          fetching: false
        }
      })

      if (form && form.current) {
        form.current.setFieldsValue({
          _templateVarAllowedKeysJson: JSON.stringify(parsed.keys),
          _templateVarKeysOptsLoaded: '1'
        })

        const slots = form.current.getFieldValue('templateVarSlots')
        if (isTemplateVarSlotsConfigured(slots)) {
          const normalized = normalizeTemplateVarSlotsIfConfigured(
            slots,
            parsed.keys
          )
          form.current.setFieldsValue({
            templateVarSlots:
              normalized && normalized.length > 0 ? normalized : undefined
          })
        }
      }
    } catch (e) {
      if (reqId !== this._templateVarFetchSeq) return
      this.setState({
        templateVarMeta: { keys: [], labelByKey: {}, fetching: false }
      })
      if (form && form.current) {
        form.current.setFieldsValue({
          _templateVarAllowedKeysJson: '[]',
          _templateVarKeysOptsLoaded: '1'
        })
      }
    }
  }

  componentDidUpdate (prevProps) {
    const cat = resolveNotifyCategoryFromBaseProps(this.props.baseProps)
    const prevCat = resolveNotifyCategoryFromBaseProps(prevProps.baseProps)
    if (
      cat !== prevCat ||
      prevProps.formKey !== this.props.formKey
    ) {
      this.fetchTemplateVarKeys()
    }

    const { form } = this.props
    const bp = this.props.baseProps || {}
    const prevBp = prevProps.baseProps || {}
    const {
      channelOptions: co = [],
      offsetUnitOptions: oo = [],
      lifecycleOptions: lo = [],
      defaultScene,
      storeOptions: so = [],
      defaultStoreId,
      editingRule
    } = bp
    const {
      channelOptions: prevCo = [],
      offsetUnitOptions: prevOo = [],
      lifecycleOptions: prevLo = [],
      defaultScene: prevDefaultScene,
      storeOptions: prevSo = [],
      editingRule: prevEditingRule
    } = prevBp

    if (!form || !form.current) return

    if (prevDefaultScene !== defaultScene) {
      form.current.setFieldsValue({ scene: defaultScene })
    }

    if (editingRule && prevEditingRule !== editingRule) {
      const parsed = ruleRecordToFormValues(editingRule)
      if (parsed) {
        form.current.setFieldsValue(parsed)
      }
    }

    if (co.length > 0 && prevCo.length === 0) {
      const cur = form.current.getFieldValue('channels')
      if (!Array.isArray(cur) || cur.length === 0) {
        form.current.setFieldsValue({ channels: [String(co[0].id)] })
      }
    }

    if (oo.length > 0 && prevOo.length === 0) {
      const cur = form.current.getFieldValue('offsetUnit')
      if (cur === undefined || cur === null || cur === '') {
        form.current.setFieldsValue({ offsetUnit: String(oo[0].id) })
      }
    }

    if (lo.length > 0 && prevLo.length === 0) {
      const cur = form.current.getFieldValue('lifecycle')
      if (cur === undefined || cur === null || cur === '') {
        form.current.setFieldsValue({ lifecycle: String(lo[0].id) })
      }
    }

    if (
      !editingRule &&
      so.length > 0 &&
      prevSo.length === 0
    ) {
      const cur = form.current.getFieldValue('storeId')
      if (cur === undefined || cur === null || cur === '') {
        const sid =
          defaultStoreId && so.some((o) => `${o.id}` === `${defaultStoreId}`)
            ? String(defaultStoreId)
            : String(so[0].id)
        form.current.setFieldsValue({ storeId: sid })
      }
    }
  }

  buildInitialValues () {
    const bp = this.props.baseProps || {}
    const {
      editingRule,
      defaultScene,
      channelOptions: co = [],
      offsetUnitOptions: oo = [],
      lifecycleOptions: lo = [],
      defaultStoreId,
      storeOptions: so = []
    } = bp

    if (editingRule) {
      const parsed = ruleRecordToFormValues(editingRule)
      if (parsed) {
        return {
          ...parsed,
          targets: Array.isArray(parsed.targets) && parsed.targets.length > 0 ? parsed.targets : [],
          _templateVarKeysOptsLoaded: '0',
          _templateVarAllowedKeysJson: ''
        }
      }
    }

    const firstChannel = co.length > 0 ? [String(co[0].id)] : []
    const firstOffsetUnit = oo.length > 0 ? String(oo[0].id) : undefined
    const firstLifecycle = lo.length > 0 ? String(lo[0].id) : undefined
    const storeIdInitial =
      defaultStoreId != null &&
      defaultStoreId !== '' &&
      Array.isArray(so) &&
      so.some((o) => `${o.id}` === `${defaultStoreId}`)
        ? String(defaultStoreId)
        : so.length > 0
          ? String(so[0].id)
          : undefined

    return {
      scene: defaultScene,
      storeId: storeIdInitial,
      enabled: true,
      offsetValue: 0,
      offsetUnit: firstOffsetUnit,
      lifecycle: firstLifecycle,
      channels: firstChannel,
      targets: [],
      _templateVarKeysOptsLoaded: '0',
      _templateVarAllowedKeysJson: ''
    }
  }

  render () {
    const { form, formKey, actType } = this.props
    const bp = this.props.baseProps || {}
    const defaultScene = bp.defaultScene
    const sceneLabel = bp.sceneLabel
    const storeOptions = bp.storeOptions || []
    const channelOptions = bp.channelOptions || []
    const targetTypeOptions = bp.targetTypeOptions || []
    const offsetUnitOptions = bp.offsetUnitOptions || []
    const lifecycleOptions = bp.lifecycleOptions || []
    const chuanglanTemplateOptions = bp.chuanglanTemplateOptions || []

    return (
      <Form
        key={formKey}
        ref={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={this.buildInitialValues()}
      >
        <div style={{ marginBottom: 14 }}>
          <Typography.Text type="secondary">通知场景：</Typography.Text>
          <Typography.Text>{sceneLabel || defaultScene || '—'}</Typography.Text>
        </div>

        <FormItem name="scene" hidden>
          <Input />
        </FormItem>

        <FormItem name="_templateVarAllowedKeysJson" hidden>
          <Input />
        </FormItem>

        <FormItem name="_templateVarKeysOptsLoaded" hidden>
          <Input />
        </FormItem>

        <FormItem
          label="门店"
          name="storeId"
          rules={[{ required: true, message: '请选择门店' }]}
        >
          <SelectF
            options={storeOptions}
            modalProps={{
              placeholder: '请选择门店',
              disabled: actType === 'edit'
            }}
          />
        </FormItem>

        <FormItem
          label="规则名称"
          name="name"
          rules={[
            { required: true, whitespace: true, message: '请输入规则名称' },
            { max: 100, message: '规则名称不能超过100个字符' }
          ]}
        >
          <Input placeholder="便于识别" allowClear />
        </FormItem>

        <FormItem
          label="目标类型"
          name="targetType"
          rules={[{ required: true, message: '请选择目标类型' }]}
          getValueProps={(value) => ({ value: scalarToTagRadioValue(value) })}
          normalize={tagRadioValueToScalar}
        >
          <TagSelectF radio options={targetTypeOptions} />
        </FormItem>

        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          定向目标（可不添加；若列表为空则不限制具体对象）。选择「员工」类目标时可按门店搜索在职成员并带出手机号；其他类型请填写目标对象 ID。
        </Typography.Text>
        <FormItem
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.storeId !== cur.storeId ||
            prev.targetType !== cur.targetType
          }
        >
          {({ getFieldValue }) => {
            const staffMode = notificationTargetIsStaff(getFieldValue('targetType'))
            const storeId = getFieldValue('storeId')
            return (
              <Form.List name="targets">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                        <FormItem
                          {...restField}
                          name={[name, 'targetId']}
                          rules={
                            staffMode
                              ? [{ required: true, message: '请选择员工' }]
                              : [{ required: true, message: '填写目标ID' }]
                          }
                        >
                          {staffMode ? (
                            <StaffAgentTargetPick storeId={storeId} listIndex={name} />
                          ) : (
                            <InputNumber
                              min={1}
                              precision={0}
                              placeholder="客户/员工ID"
                              style={{ width: 160 }}
                            />
                          )}
                        </FormItem>
                        <FormItem {...restField} name={[name, 'phone']}>
                          <Input
                            placeholder={
                              staffMode ? '选人后带出，可改' : '手机（可选）'
                            }
                            style={{ width: 160 }}
                            allowClear
                          />
                        </FormItem>
                        <FormItem {...restField} name={[name, 'targetName']} hidden>
                          <Input />
                        </FormItem>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <FormItem>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        添加定向目标
                      </Button>
                    </FormItem>
                  </>
                )}
              </Form.List>
            )
          }}
        </FormItem>

        <Space align="start" style={{ width: '100%' }} size="middle" wrap>
          <FormItem
            label="触发间隔数值"
            name="offsetValue"
            rules={[{ required: true, message: '请填写间隔' }]}
            style={{ minWidth: 140 }}
          >
            <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="如 0 表示立即" />
          </FormItem>
          <FormItem
            label="间隔单位"
            name="offsetUnit"
            rules={[{ required: true, message: '请选择单位' }]}
            style={{ minWidth: 160 }}
          >
            <SelectF
              options={offsetUnitOptions}
              modalProps={{ placeholder: '单位' }}
            />
          </FormItem>
        </Space>

        <FormItem
          label="触发条件"
          name="lifecycle"
          rules={[{ required: true, message: '请选择触发条件' }]}
          getValueProps={(value) => ({ value: scalarToTagRadioValue(value) })}
          normalize={tagRadioValueToScalar}
        >
          <TagSelectF radio options={lifecycleOptions} />
        </FormItem>

        <FormItem
          label="通知渠道"
          name="channels"
          rules={[
            {
              validator: (_, val) => {
                if (Array.isArray(val) && val.length > 0) {
                  return Promise.resolve()
                }
                return Promise.reject(new Error('请至少选择一种通知渠道'))
              }
            }
          ]}
        >
          <TagSelectF options={channelOptions} />
        </FormItem>

        <FormItem noStyle shouldUpdate={(prev, cur) => prev.channels !== cur.channels}>
          {({ getFieldValue }) => {
            const needSms = channelsIncludeSms(getFieldValue('channels'))
            return (
              <FormItem
                label="创蓝短信模板ID"
                name="templateId"
                rules={
                  needSms
                    ? [{ required: true, message: '选择短信渠道时必填创蓝短信模板' }]
                    : []
                }
              >
                <SelectF
                  options={chuanglanTemplateOptions}
                  modalProps={{
                    placeholder: needSms ? '请选择创蓝短信模板' : '未选短信时可不填',
                    allowClear: true
                  }}
                />
              </FormItem>
            )
          }}
        </FormItem>

        <FormItem
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.channels !== cur.channels ||
            prev.templateId !== cur.templateId ||
            prev.templateVarSlots !== cur.templateVarSlots ||
            prev._templateVarKeysOptsLoaded !== cur._templateVarKeysOptsLoaded
          }
        >
          {({ getFieldValue }) => {
            const needTplVar =
              channelsIncludeSms(getFieldValue('channels')) &&
              String(getFieldValue('templateId') || '').trim() !== ''
            if (!needTplVar) return null
            return (
              <FormItem
                label="模版内容变量顺序"
                name="templateVarSlots"
                extra="仅当创蓝短信模板正文含占位符时需要设置；未点选按钮则不会提交默认顺序。"
                rules={[
                  {
                    validator: async () => {
                      if (getFieldValue('_templateVarKeysOptsLoaded') !== '1') {
                        return Promise.reject(
                          new Error('模版变量选项加载中，请稍候再提交')
                        )
                      }
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <TemplateVarSlotsEditor
                  key={`${String(formKey)}-${resolveNotifyCategoryFromBaseProps(bp)}`}
                  allowedKeys={
                    this.state.templateVarMeta.fetching
                      ? undefined
                      : this.state.templateVarMeta.keys
                  }
                  labelByKey={this.state.templateVarMeta.labelByKey}
                  fetching={this.state.templateVarMeta.fetching}
                />
              </FormItem>
            )
          }}
        </FormItem>

        <FormItem label="是否启用" name="enabled" valuePropName="checked">
          <Switch checkedChildren="启用" unCheckedChildren="禁用" />
        </FormItem>
      </Form>
    )
  }
}

export default NotificationRuleForm
